import { Box, useTheme, IconButton, Tooltip, Button, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useBatchEdit } from 'components/BatchEdit/context';
import { useCallback, useMemo } from 'react';
import { useSearchItems } from './SearchItemsProvider';
import { getPrimaryBgColor } from 'utility/styles';
import {
  checkBatchEditSearchPermission,
  checkSiteAdminPermission,
  PermissionInfo,
} from 'utility/permissions';
import { useCosmos } from 'providers/contexts/CosmosContext';
import { bloxValues } from 'themes/constants';
import { useTranslation } from 'react-i18next';
import { useRelationshipsSearchSafe } from 'screens/editorial/asset/components/common/RelatedAssetsDrawer/hooks/useRelationshipsSearchSafe';
import { EditorialAssetSearchResultItem } from 'utility/types/dataProvider';
import { AddToRelationshipButton } from 'screens/editorial/asset/components/common/RelatedAssetsDrawer/components/AddToRelationshipButton';

export const EditorialSearchFooter = () => {
  const theme = useTheme();
  const { sessionConfig } = useCosmos();
  const { selectedItemIds, clearSelectedItems, selectedItems } =
    useSearchItems<EditorialAssetSearchResultItem>();
  const { toggleDialog } = useBatchEdit();
  const { t } = useTranslation();

  // Get the relationships search context - will be undefined if not in modal
  const relationshipsContext = useRelationshipsSearchSafe();

  const {
    onInsert,
    hasInsertCapability,
    onCloseModal,
    currentRecordType,
    isUseAssetMode,
    footerActionMode,
  } = relationshipsContext || {};

  const { granted: userIsAdmin } = checkSiteAdminPermission(sessionConfig);
  const { granted: canBatchEditSearch, name: batchPermName } =
    checkBatchEditSearchPermission(sessionConfig);
  const selectedItemCount = selectedItemIds.ids.size;
  const hasSelectedItems = Boolean(selectedItemCount > 0);

  const isAddOnlyMode = Boolean(isUseAssetMode && footerActionMode === 'addOnly');

  // Show Insert/Use Selected button for:
  // - Article/Collection types (standard insert mode)
  // - Any type when in "use asset" mode (e.g., FileFieldInput selection)
  const showInsertButton =
    !isAddOnlyMode &&
    hasInsertCapability &&
    onInsert &&
    (isUseAssetMode ||
      currentRecordType === 'article' ||
      currentRecordType === 'collection');

  // Button text: "Use Selected" when in use asset mode, "Insert Selected" otherwise
  const insertButtonText = isUseAssetMode
    ? t('blox.actions.use_selected')
    : t('blox.actions.insert_selected');

  const btnState: PermissionInfo = useMemo(() => {
    if (userIsAdmin) {
      return {
        info: undefined,
        disabled: false,
      };
    }
    if (!canBatchEditSearch) {
      return {
        info: t('blox.permissions.msg.required_short', { name: batchPermName }),
        disabled: true,
      };
    }
    return {
      info: undefined,
      disabled: false,
    };
  }, [t, canBatchEditSearch, batchPermName, userIsAdmin]);

  const handleClearSelectedItems = useCallback(() => {
    clearSelectedItems();
  }, [clearSelectedItems]);

  const handleToggleDialog = useCallback(() => {
    toggleDialog();
  }, [toggleDialog]);

  const handleInsertSelected = useCallback(() => {
    if (!onInsert || !selectedItems || selectedItems.length === 0) {
      return;
    }

    // Call the insert handler - Allow it to handle its own notifications
    // Collection insert shows duplicate info if applicable, Article insert shows success
    onInsert(selectedItems);
    clearSelectedItems();
  }, [onInsert, selectedItems, clearSelectedItems]);

  const handleAddToSelected = useCallback(() => {
    if (!onInsert || !selectedItems || selectedItems.length === 0) {
      return;
    }

    const selectedItem = selectedItems[selectedItems.length - 1];
    if (!selectedItem) {
      return;
    }

    onInsert([selectedItem]);
    clearSelectedItems();
  }, [onInsert, selectedItems, clearSelectedItems]);

  return (
    <Box
      aria-label="search footer"
      aria-hidden={!hasSelectedItems}
      sx={{
        display: hasSelectedItems ? 'block' : 'none',
        backgroundColor: theme.palette.background.paper,
      }}
    >
      <Box
        aria-label="selected items"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          px: 2,
          backgroundColor: getPrimaryBgColor(theme),
          borderTop: `1px solid ${getPrimaryBgColor(theme, true)}`,
          boxShadow: theme.shadows[1],
          minHeight: 52,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            alignItems: 'center',
          }}
        >
          <Typography
            sx={{
              fontSize: bloxValues.regularFontSizeRem,
              color: theme.palette.text.primary,
            }}
          >
            {t('blox.general.count_selected', {
              count: selectedItemIds.ids.size,
            })}
          </Typography>

          {/* Insert/Use Selected button */}
          {showInsertButton && (
            <Button
              data-testid="editorial-search-insert-selections"
              aria-label="insert selections"
              variant="contained"
              onClick={handleInsertSelected}
              disabled={selectedItemCount === 0}
              disableElevation
            >
              {insertButtonText}
            </Button>
          )}

          {/* Add to in single-select modal mode */}
          {isAddOnlyMode && (
            <Button
              data-testid="editorial-search-add-to-selections"
              aria-label="add to selections"
              variant="contained"
              onClick={handleAddToSelected}
              disabled={selectedItemCount === 0}
              disableElevation
            >
              {t('blox.actions.insert_selected')}
            </Button>
          )}

          {/* Add to Relationships options when in modal context */}
          {relationshipsContext && !isAddOnlyMode && (
            <AddToRelationshipButton onClose={onCloseModal} />
          )}

          {/* Batch Edit */}
          {!isAddOnlyMode && (
            <Tooltip
              title={btnState.info}
              placement="top"
            >
              <span>
                <Button
                  data-testid="editorial-search-batch-edit-selections"
                  aria-label="batch edit selections"
                  variant="contained"
                  onClick={handleToggleDialog}
                  disabled={btnState.disabled}
                  disableElevation
                >
                  {t('blox.batch_edit.actions.edit')}
                </Button>
              </span>
            </Tooltip>
          )}
        </Box>
        <Box>
          <Tooltip title={t('blox.batch_edit.actions.clear')}>
            <IconButton
              aria-label={t('blox.batch_edit.actions.clear')}
              onClick={handleClearSelectedItems}
            >
              <CloseIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
    </Box>
  );
};
